/**
 * Echo Hunt -- live (DB-backed) wiring for the shared reshape + round bullet pool (P7).
 *
 * Per the ground rules (§16.0), the logic lives in the pure `Reshape.ts`; this thin `*_Live.ts` wrapper
 * is where the corpus rows and the user's profile settings are read.
 *
 * Two things are assembled here:
 *  1. the REAL bullet pool from the cached, usable corpus rows (`echoCorpusBullets`), with each bullet's
 *     DREAM FAMILY resolved (§4.5 "treat same-night dreams as one family" -- a `JournalEntry` IS one
 *     night, so the family key is the entry id embedded in each bullet id);
 *  2. the profile-derived settings the pool builder needs (seed words, cliché blacklist, banned terms,
 *     the fake distributions).
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §4.5, §6, §16.2 P7.
 */
import {EchoCorpusBullet} from "../Server/DB/echoCorpusBullets/@EchoCorpusBullet.js";
import {GetMyEchoCorpusBullets} from "./Index_Live.js";
import {IsSeedWordlistReady, GetFakeDistributions, GetUsableCorpusRows} from "./FakeGenerator_Live.js";
import {GetSeedWordlist} from "./Wordlist_Live.js";
import {liveLLMBackend} from "./LLM_Live.js";
import {LLMCallCounter} from "./LLM.js";
import {JudgeStory} from "./Judge.js";
import {GetJudgeSettings, GetMyEchoProfile} from "./Profiles_Live.js";
import {
	BuildBulletPool,
	BuildBulletPoolOptions,
	BulletPoolResult,
	RealBulletForPool,
	RealBulletWeight,
	ReshapeBullet,
	ReshapeOptions,
	ReshapeResult,
	SampleReshapeWordCount,
	SampleDistinctWords,
} from "./Reshape.js";

/** The dream family a bullet belongs to (§4.5). Bullet ids are `entryId/segmentId/<ordinal>`, and one
 * `JournalEntry` is one NIGHT (its segments are the night's separate dreams), so the entry id is
 * exactly the "same-night" key the plan wants. Falls back to the whole id when it does not parse, which
 * keeps a malformed id its own family rather than lumping every bad id together. */
export function GetBulletFamilyId(bulletId: string|n): string {
	const id = bulletId ?? "";
	const firstSlash = id.indexOf("/");
	return firstSlash == -1 ? id : id.substring(0, firstSlash);
}

/** The raw journal text of every bullet in each dream, keyed by `dreamId` -- the leak references (2026-10-21).
 * The WHOLE dream rather than just the bullet, because the index pass resolves references by borrowing wording
 * from neighbouring bullets, so a copied run can come from one of them. Built from ALL cached rows (filtered
 * ones included): a filtered sibling is still the user's own wording. */
export function GetRawTextsByDream(rows: EchoCorpusBullet[] = GetMyEchoCorpusBullets()): Map<string, string[]> {
	const result = new Map<string, string[]>();
	for (const row of rows) {
		const raw = row.rawText?.trim();
		if (raw == null || raw.length == 0) continue;
		const list = result.get(row.dreamId) ?? [];
		list.push(raw);
		result.set(row.dreamId, list);
	}
	return result;
}

/** Maps one cached corpus row to the pure pool-builder shape. `rawTextsByDream` supplies the leak references;
 * without it, only the row's own raw text is used. */
export function RowToRealBulletForPool(row: EchoCorpusBullet, rawTextsByDream?: Map<string, string[]>|n): RealBulletForPool {
	const ownRaw = row.rawText?.trim();
	return {
		leakReferences: rawTextsByDream?.get(row.dreamId) ?? (ownRaw != null && ownRaw.length > 0 ? [ownRaw] : []),
		id: row.id,
		// the RESHAPED text is built from the indexed, house-style rewrite (§4.2), never the raw journal
		// text -- the raw text carries the user's own punctuation habits (§4.3)
		text: row.selfContainedText ?? "",
		familyId: GetBulletFamilyId(row.bulletId ?? row.id),
		exposureRoundCount: row.exposureRoundCount ?? 0,
		exposureRevealCount: row.exposureRevealCount ?? 0,
	};
}

/** The usable real bullets for one round (§4.5), as the pure sampler wants them. */
export function GetRealBulletsForPool(): RealBulletForPool[] {
	const rawTextsByDream = GetRawTextsByDream();
	return GetUsableCorpusRows()
		.map(row=>RowToRealBulletForPool(row, rawTextsByDream))
		.filter(a=>a.text.trim().length > 0);
}

/** True when a round could actually be built: seed words are loaded and there is at least one usable
 * real bullet. Both are reachable "empty" states (the word list is a 1.9 MB fetch; the corpus needs an
 * index run), and a pool silently built from nothing would be far worse than a disabled button. */
export function IsBulletPoolReady(): boolean {
	return IsSeedWordlistReady() && GetRealBulletsForPool().length > 0;
}

/** Builds one round's bullet pool with the live corpus + profile + network backend (§16.2 P7). Returns
 * null when the corpus or the word list is not ready, so the UI can explain why.
 *
 * P10 adds rejection sampling (§5.5): when the profile enables it, each fake is generated several times and
 * the candidate the HELD-OUT judge rates most human is kept. The held-out judge is the right rater on
 * purpose -- choosing a candidate against the judge that will later judge it would be Goodhart'd by
 * construction (§9.5, §17). */
export async function BuildBulletPool_Live(options: {N: number, X: number, rng: () => number, seedingEnabled?: boolean, generatorModels?: string[]|n, reshapeModel?: string|n, counter?: LLMCallCounter|n, maxAttempts?: number|n, slotIdFactory?: (()=>string)|n, rejectionSampling?: boolean|n}): Promise<BulletPoolResult|null> {
	const profile = GetMyEchoProfile();
	const wordlist = GetSeedWordlist(profile);
	if (wordlist == null || wordlist.words.length == 0) return null;

	const samplingEnabled = options.rejectionSampling ?? GetJudgeSettings().rejectionSampling;

	const poolOptions: BuildBulletPoolOptions = {
		N: options.N,
		X: options.X,
		corpus: GetRealBulletsForPool(),
		wordlist: wordlist.words,
		distributions: GetFakeDistributions(),
		rng: options.rng,
		seedingEnabled: options.seedingEnabled,
		blacklist: (profile?.clicheBlacklist ?? []).map(a=>a.phrase),
		bannedWords: profile?.bannedTerms ?? [],
		generatorModels: options.generatorModels,
		reshapeModel: options.reshapeModel,
		counter: options.counter,
		maxAttempts: options.maxAttempts ?? undefined,
		slotIdFactory: options.slotIdFactory ?? undefined,
		rejectionSampling: samplingEnabled
			? {
				candidates: Math.max(2, GetJudgeSettings().rejectionSamplingCandidates ?? 3),
				rate: text=>RateFakeWithHeldOutJudge_Live(text, options.counter),
			}
			: null,
	};
	return BuildBulletPool(liveLLMBackend, poolOptions);
}

/** One candidate's "how human does this read" score, from the held-out judge (§5.5, §9.5). Lower = more
 * human, because the judge's number is P(dream-sourced) and a good fake should look LESS like a dream.
 *
 * A judge failure returns .5 ("no opinion"), which keeps every candidate comparable rather than letting a
 * transport hiccup decide the winner. */
async function RateFakeWithHeldOutJudge_Live(text: string, counter?: LLMCallCounter|n): Promise<number> {
	const judged = await JudgeStory(liveLLMBackend, {
		bullets: [{slotId: "candidate", text}],
		model: GetMyEchoProfile()?.modelRoles?.heldOutJudge ?? null,
		counter,
	});
	return judged.scores.candidate ?? .5;
}

/** Reshapes ONE bullet with the live settings (the dev tool's per-bullet path, §16.2 P7 "a dev tool
 * shows a pool of 8 bullets"). Returns null when the word list is not loaded yet. */
export async function ReshapeBullet_Live(bulletText: string, options: {rng: () => number, seedWords?: string[]|n, counter?: LLMCallCounter|n, model?: string|n, maxAttempts?: number|n}): Promise<{result: ReshapeResult, seedWords: string[]}|null> {
	const profile = GetMyEchoProfile();
	const wordlist = GetSeedWordlist(profile);
	if (wordlist == null || wordlist.words.length == 0) return null;

	const seedWords = options.seedWords ?? SampleDistinctWords(wordlist.words, SampleReshapeWordCount(bulletText, options.rng), options.rng);

	const reshapeOptions: ReshapeOptions = {
		seedWords,
		bannedWords: profile?.bannedTerms ?? [],
		blacklist: (profile?.clicheBlacklist ?? []).map(a=>a.phrase),
		model: options.model,
		counter: options.counter,
		maxAttempts: options.maxAttempts ?? undefined,
	};
	const result = await ReshapeBullet(liveLLMBackend, bulletText, reshapeOptions);
	return {result, seedWords};
}

/** Debug/inspection: the corpus rows a round would actually sample from, with the exact fallowness
 * weight the §4.5 sampler uses. Exported for the Settings-tab dev tool, so "why is this bullet never
 * chosen?" is answerable instead of looking like a bug. */
export function GetPoolCandidatesWithWeights(): {row: EchoCorpusBullet, familyId: string, weight: number}[] {
	const rows = GetUsableCorpusRows();
	return rows.map(row=>{
		const poolBullet = RowToRealBulletForPool(row);
		return {row, familyId: poolBullet.familyId, weight: RealBulletWeight(poolBullet)};
	});
}

/** The full cached corpus (including filtered rows), for the dev tool's diagnostics. */
export function GetAllCorpusRows(): EchoCorpusBullet[] {
	return GetMyEchoCorpusBullets();
}
